import { EventrDate, IOptionsWithTypes } from '@eventr/common'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useShallow } from 'zustand/react/shallow'
import {
    editHeaderTranslation,
    editItineraryFieldsTranslation,
    editItineraryTranslation,
    generalTranslation,
} from '../../../i18n/translateHelpers'
import { useBoundStore } from '../../../state/bound.store'
import { getUsersCommaSeparated } from '../../../state/user.slice'
import { FormAddressInput } from '../formComponents/formAddressInput'
import { PremiumAdvanceFields } from '../formComponents/formAdvanceFields'
import { FormDateTime } from '../formComponents/formDateTime'
import { FormEditBookings } from '../formComponents/formEditBookings'
import { AppFormImageSelect } from '../formComponents/formImageSelect'
import { FormJoinLeaveButton } from '../formComponents/formJoinBtn'
import { FormText } from '../formComponents/formText'
import { PremiumFormUploadDocs } from '../formComponents/formUploadDocs'
import { SectionEdit } from '../formComponents/section'
import { BaseEditItem } from '../subPages/baseEditItem'
import { ActivityValidationSchema } from './activityEditItem'
import { DateFieldsValidator } from './dateFieldValidator'

export const ActivityMainForm = () => {
    const { userId } = useBoundStore(
        useShallow((s) => {
            return { userId: s.wpUser?.id }
        })
    )
    const {
        control,
        watch,
        formState: { isDirty },
    } = useFormContext<ActivityValidationSchema>()

    // get new from url params
    const newItem = new URLSearchParams(window.location.search).get('new')

    const isNew = newItem === 'true'
    const attendeesList = watch('attendees_list')
    const name = watch('activity_name')
    const type = watch('type', 'general')

    const userList = useMemo(() => {
        if (!attendeesList || !Array.isArray(attendeesList)) return ''
        const flatUsers = attendeesList.reduce<IOptionsWithTypes[]>(
            (acc, attendee) => {
                if (attendee.users && Array.isArray(attendee.users)) {
                    const users = attendee.users.map(
                        (user: IOptionsWithTypes) => ({
                            ...user,
                        })
                    ) as any
                    return [...acc, ...users]
                }
                return acc
            },
            []
        )

        return getUsersCommaSeparated(flatUsers)
    }, [attendeesList])

    const isJoined = useMemo(() => {
        if (!attendeesList || !Array.isArray(attendeesList)) return false
        const joined = attendeesList.some((attendee) => {
            if (attendee.users && Array.isArray(attendee.users)) {
                return attendee.users.some(
                    (user: IOptionsWithTypes) => user.option === userId
                )
            }
            return false
        })
        return joined
    }, [attendeesList, userId])

    return (
        <BaseEditItem
            isNewItem={isNew}
            saveAllowed={isDirty}
            deleteButtonText={editItineraryTranslation('Delete Activity')}
            headerText={
                isNew
                    ? editItineraryTranslation('New Activity')
                    : generalTranslation('Edit') + ' ' + name
            }
        >
            <DateFieldsValidator />
            {type === 'general' && (
                <FormJoinLeaveButton
                    keyToUsers="attendees_list"
                    isJoined={isJoined}
                    joinText={editItineraryTranslation('Join Activity')}
                    leaveText={editItineraryTranslation('Leave Activity')}
                    sectionId={8}
                />
            )}

            <SectionEdit
                headerIcon="Itinerary"
                headerText={editHeaderTranslation('Activity Details')}
            />
            <FormText
                required
                style={{ marginTop: '16px' }}
                fieldLabel={editItineraryFieldsTranslation('801')}
                control={control as any}
                name="activity_name"
            />
            <FormText
                fieldLabel={editItineraryFieldsTranslation('805')}
                control={control as any}
                name="location"
            />
            {type === 'general' && (
                <>
                    <FormDateTime
                        fieldLabel={editItineraryFieldsTranslation('802')}
                        required
                        control={control}
                        name="start"
                    />
                    <FormDateTime
                        fieldLabel={editItineraryFieldsTranslation('803')}
                        control={control}
                        minDate={new EventrDate(watch('start'))}
                        name="end"
                    />
                </>
            )}
            <FormAddressInput
                fieldLabel={editItineraryFieldsTranslation('804')}
                control={control as any}
                name="address"
            />
            <SectionEdit
                headerIcon="Additional_Information"
                headerText={editHeaderTranslation('Additional Information')}
            />
            <FormText
                fieldLabel={editItineraryFieldsTranslation('813')}
                control={control as any}
                name="website"
            />
            <FormText
                fieldLabel={editItineraryFieldsTranslation('807')}
                control={control as any}
                name="additional_information"
                multiline
                rows={2}
            />
            <PremiumFormUploadDocs
                fieldLabel={editItineraryFieldsTranslation('Documents')}
                control={control}
                name={`documents`}
            />
            <PremiumAdvanceFields control={control as any} name="isAdvance">
                <AppFormImageSelect
                    name="cardImages.0.url"
                    control={control as any}
                    height="80px"
                    label={editItineraryFieldsTranslation('Card Image')}
                    helperText={
                        editItineraryTranslation('Recommended ratio') + ' 1:5'
                    }
                />
                <AppFormImageSelect
                    name="images.0.url"
                    control={control as any}
                    height="80px"
                    label={editItineraryFieldsTranslation('Page Image')}
                />
                <FormText
                    fieldLabel={editItineraryFieldsTranslation('description')}
                    control={control as any}
                    name="description"
                    multiline
                    rows={2}
                />
            </PremiumAdvanceFields>
            {type === 'general' && (
                <FormEditBookings
                    btnText={editHeaderTranslation('Edit Attendees')}
                    currentUsersList={userList}
                />
            )}
        </BaseEditItem>
    )
}
